import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { systemSettings } from '@/mocks/settings';
import { requestPasswordReset, resetPassword, verifyResetCode } from '@/services/authService';
import Alert from '@/components/base/Alert';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import AuthLayout from '../components/AuthLayout';
import PasswordField from '../components/PasswordField';

type Step = 'email' | 'code' | 'password' | 'success';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const steps: { id: Step; label: string }[] = [
    { id: 'email', label: t('auth.reset.stepEmail') },
    { id: 'code', label: t('auth.reset.stepCode') },
    { id: 'password', label: t('auth.reset.stepPassword') },
  ];
  const activeIndex = steps.findIndex((s) => s.id === step);

  const requestCode = async () => {
    setError(null);
    setFieldError(null);
    const trimmed = email.trim();
    if (!trimmed) return setFieldError(t('common.required'));
    if (!EMAIL_RE.test(trimmed)) return setFieldError(t('common.invalidEmail'));
    setLoading(true);
    await requestPasswordReset(trimmed);
    setLoading(false);
    setStep('code');
    return undefined;
  };

  const onEmailSubmit = (e: FormEvent) => {
    e.preventDefault();
    void requestCode();
  };

  const onCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldError(null);
    if (!code.trim()) return setFieldError(t('common.required'));
    setLoading(true);
    const result = await verifyResetCode(email.trim(), code.trim());
    setLoading(false);
    if (result.ok) {
      setStep('password');
      return;
    }
    setError(
      result.error === 'expired' ? t('auth.reset.errorExpiredCode') : t('auth.reset.errorInvalidCode'),
    );
  };

  const resend = async () => {
    setError(null);
    setCode('');
    setLoading(true);
    await requestPasswordReset(email.trim());
    setLoading(false);
  };

  const onPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldError(null);
    if (!password) return setFieldError(t('common.required'));
    if (password.length < 8) return setFieldError(t('auth.register.passwordHint'));
    if (password !== confirm) return setFieldError(t('auth.reset.mismatch'));
    setLoading(true);
    const result = await resetPassword(email.trim(), code.trim(), password);
    setLoading(false);
    if (result.ok) setStep('success');
    else setError(result.error === 'expired' ? t('auth.reset.errorExpiredCode') : t('auth.reset.errorInvalidCode'));
  };

  return (
    <AuthLayout>
      <div className="rounded-lg border border-background-200 bg-background-50 p-6 shadow-card animate-fade-in-up md:p-8">
        <h1 className="text-xl font-bold text-foreground-950 md:text-2xl">{t('auth.reset.title')}</h1>

        {step !== 'success' ? (
          <ol className="mt-5 flex items-center gap-2">
            {steps.map((s, idx) => (
              <li key={s.id} className="flex flex-1 items-center gap-2">
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    idx <= activeIndex
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-200 text-foreground-500'
                  }`}
                >
                  {idx + 1}
                </span>
                <span
                  className={`hidden truncate text-xs sm:block ${
                    idx <= activeIndex ? 'text-foreground-800' : 'text-foreground-400'
                  }`}
                >
                  {s.label}
                </span>
                {idx < steps.length - 1 ? (
                  <span className="h-px flex-1 bg-background-200" aria-hidden="true" />
                ) : null}
              </li>
            ))}
          </ol>
        ) : null}

        {error ? (
          <div className="mt-5">
            <Alert variant="error">{error}</Alert>
          </div>
        ) : null}

        {step === 'email' ? (
          <form className="mt-6 space-y-4" onSubmit={onEmailSubmit} noValidate>
            <p className="text-sm text-foreground-500">{t('auth.reset.emailIntro')}</p>
            <Input
              id="reset-email"
              name="email"
              label={t('auth.login.email')}
              type="email"
              ltr
              leadingIcon="ri-mail-line"
              requiredMark
              value={email}
              error={fieldError ?? undefined}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
              {t('auth.reset.sendCode')}
            </Button>
          </form>
        ) : null}

        {step === 'code' ? (
          <form className="mt-6 space-y-4" onSubmit={onCodeSubmit} noValidate>
            <p className="text-sm text-foreground-500">{t('auth.reset.codeIntro')}</p>
            <div className="rounded-md bg-background-100 px-3.5 py-3">
              <p className="text-xs text-foreground-500">{t('auth.reset.codeSentTo')}</p>
              <p className="lb-ltr-field mt-0.5 text-sm font-semibold text-foreground-900">{email}</p>
            </div>
            <Input
              id="reset-code"
              name="code"
              label={t('auth.reset.code')}
              placeholder={t('auth.reset.codePlaceholder')}
              ltr
              leadingIcon="ri-shield-keyhole-line"
              inputMode="numeric"
              hint={t('auth.reset.validity', { minutes: systemSettings.otpValidityMinutes })}
              requiredMark
              value={code}
              error={fieldError ?? undefined}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
              {t('auth.reset.verify')}
            </Button>
            <button
              type="button"
              onClick={() => void resend()}
              disabled={loading}
              className="w-full text-center text-sm font-medium text-accent-600 hover:text-accent-700 disabled:opacity-50"
            >
              {t('auth.reset.resend')}
            </button>
          </form>
        ) : null}

        {step === 'password' ? (
          <form className="mt-6 space-y-4" onSubmit={onPasswordSubmit} noValidate>
            <PasswordField
              id="reset-new"
              name="new_password"
              label={t('auth.reset.newPassword')}
              autoComplete="new-password"
              requiredMark
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              showLabel={t('auth.login.showPassword')}
              hideLabel={t('auth.login.hidePassword')}
            />
            <PasswordField
              id="reset-confirm"
              name="confirm_password"
              label={t('auth.reset.confirmPassword')}
              autoComplete="new-password"
              requiredMark
              value={confirm}
              error={fieldError ?? undefined}
              onChange={(e) => setConfirm(e.target.value)}
              showLabel={t('auth.login.showPassword')}
              hideLabel={t('auth.login.hidePassword')}
            />
            <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
              {t('auth.reset.submit')}
            </Button>
          </form>
        ) : null}

        {step === 'success' ? (
          <div className="mt-6 text-center">
            <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <i className="ri-checkbox-circle-line text-3xl leading-none" aria-hidden="true" />
            </span>
            <h2 className="text-lg font-semibold text-foreground-950">{t('auth.reset.successTitle')}</h2>
            <p className="mt-1.5 text-sm text-foreground-600">{t('auth.reset.successText')}</p>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              className="mt-6"
              onClick={() => navigate('/login', { replace: true })}
            >
              {t('auth.reset.toLogin')}
            </Button>
          </div>
        ) : null}

        {step !== 'success' ? (
          <div className="mt-6 border-t border-background-200 pt-5">
            <Link to="/login" className="text-sm font-medium text-foreground-600 hover:text-foreground-900">
              {t('auth.reset.backToLogin')}
            </Link>
          </div>
        ) : null}
      </div>

      <div className="mt-5">
        <Alert variant="info" title={t('common.demoData')}>
          {t('auth.reset.demoNotice')}
        </Alert>
      </div>
    </AuthLayout>
  );
}